import React from "react";
import { DialogProps, ChannelInfo } from "../../types/dialog-types";
import { tagSelectStyles as s } from "./tagSelectDialogStyles";
import { getTigerResource } from "../../utils/tigerResource";

interface DiagSelectTagDialogProps extends DialogProps {
  tags?: ChannelInfo[];
}

/** Tag-selection dialog for the diagnostics "Tools → Trend Graph" action.
 *  Themed to match the Graph Channel Selection dialog; labels via
 *  getTigerResource. */
export function DiagSelectTagDialog({
  open,
  onClose,
  onApply,
  tags = [],
}: DiagSelectTagDialogProps) {
  const [selected, setSelected] = React.useState<Set<number>>(new Set());

  React.useEffect(() => {
    if (open) setSelected(new Set());
  }, [open]);

  const toggleRow = (idx: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const handleOk = () => {
    const chosen = tags.filter((_, idx) => selected.has(idx));
    if (chosen.length > 0) {
      onApply?.({ tags: chosen });
    }
    onClose();
  };

  if (!open) return null;
  const title = getTigerResource("TIGER_GRAPH_CHANNEL_GRAPH_TITLE");

  return (
    <div style={s.overlay}>
      <div style={s.dialog} role="dialog" aria-modal="true" aria-label={title}>
        <div style={s.header}>
          <span style={s.title}>{title}</span>
          <button style={s.closeBtn} onClick={onClose}>
            &#x2715;
          </button>
        </div>

        <div style={s.body}>
          <div style={s.section}>
            <div style={s.tableWrap}>
              <div className="list-control" style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 180 }}>{getTigerResource("TIGER_EXPORT_CHANNEL_NAME")}</th>
                      <th style={{ width: 300 }}>{getTigerResource("TIGER_EXPORT_CHANNEL_DESCR")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tags.map((item, idx) => (
                      <tr
                        key={idx}
                        className={selected.has(idx) ? "selected" : ""}
                        onClick={() => toggleRow(idx)}
                        onDoubleClick={() => {
                          onApply?.({ tags: [item] });
                          onClose();
                        }}
                      >
                        <td>{item.tag}</td>
                        <td>{item.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div style={s.footer}>
          <button
            style={{ ...s.btnPrimary, opacity: selected.size === 0 ? 0.4 : 1 }}
            onClick={handleOk}
            disabled={selected.size === 0}
          >
            {getTigerResource("TIGER_RES_OK")}
          </button>
          <button style={s.btnSecondary} onClick={onClose}>
            {getTigerResource("TIGER_RES_CANCEL")}
          </button>
        </div>
      </div>
    </div>
  );
}
